import { InputAdornment, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { parseContactCaptureHexColor } from '../../helpers';
import { ColorSwatch, ColorSwatchNativeInput } from './ColorSwatch.styles';

type Props = {
  label: string;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
};

/**
 * Typed text is committed once it is a full six-digit hex color, so the preview never flashes a
 * partial value; a three-digit shorthand is expanded when the field loses focus. Invalid text is
 * only flagged on blur, and the preview keeps the last valid color meanwhile.
 */
export const ContactCaptureColorField = ({ label, value, disabled, onChange }: Props) => {
  const [draft, setDraft] = useState(value);
  const [touched, setTouched] = useState(false);
  const parsed = parseContactCaptureHexColor(draft);
  const showError = touched && !parsed;

  useEffect(() => {
    setDraft(current => (parseContactCaptureHexColor(current) === value.toLowerCase() ? current : value));
  }, [value]);

  const commit = (color: string | null) => {
    if (color && color !== value.toLowerCase()) onChange(color);
  };

  const update = (next: string) => {
    setDraft(next);
    setTouched(false);
    if (/^#?[0-9a-f]{6}$/i.test(next.trim())) commit(parseContactCaptureHexColor(next));
  };

  const finish = () => {
    setTouched(true);
    if (!parsed) return;
    commit(parsed);
    setDraft(parsed);
  };

  return (
    <TextField
      label={label}
      value={draft}
      disabled={disabled}
      onChange={event => update(event.target.value)}
      onBlur={finish}
      error={showError}
      helperText={showError ? 'Enter a hex color, for example #1A2B3C.' : undefined}
      fullWidth
      inputProps={{ 'aria-label': `${label} hex code`, spellCheck: false, maxLength: 7 }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <ColorSwatch color={parsed || value} disabled={disabled}>
              <ColorSwatchNativeInput
                type="color"
                value={parsed || value}
                disabled={disabled}
                onChange={event => update(event.target.value)}
                aria-label={`${label} picker`}
              />
            </ColorSwatch>
          </InputAdornment>
        ),
      }}
    />
  );
};
